import { useEffect, useMemo, useState } from "react";

import { Button, Checkbox, Divider, FormControl, FormErrorMessage, FormLabel, Input, Switch, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCompanyType from "@/components/Selects/SelectCompanyType";
import SelectCountry from "@/components/Selects/SelectCountry";
import { inspectorPersonSchema } from "@/schemas/inspectorPersonSchema";
import { getInspectorPersonById, InspectorPerson, postInspectorPerson, putInspectorPerson } from "@/services/inspectorPersonService";

import '../styles/Form.css';
import EditFile from "@/components/Fields/EditFile";

const EditInspectorPerson = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const location = useLocation();
    const toast = useToast();

    const { id } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataDetail, setDataDetail] = useState<InspectorPerson | null>(null);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [IDCardFile, setIDCardFile] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    //const [previewFile, setPreviewFile] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<any[]>([]);
    //const [showEditImage, setShowEditImage] = useState(false);
    const [status, setStatus] = useState(false);
    const [title, setTitle] = useState("");

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Save Inspector?");
        setConfirmationTest("Are you sure you want to save?");
        setConfirmationTitleButton("Save Inspector");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataAdd = () => {
        const data = new FormData();
        data.append('InspectorID', dataEdit.idNumber);
        data.append('FullName', dataEdit.name);
        data.append('Type', dataEdit.type);
        data.append('Country', dataEdit.country || '');
        data.append('Oil', selectedCategory.find((x: string) => x === 'oil') ? 'true' : 'false');
        data.append('Chemicals', selectedCategory.find((x: string) => x === 'chemicals') ? 'true' : 'false');
        data.append('Gas', selectedCategory.find((x: string) => x === 'gas') ? 'true' : 'false');
        data.append('file', IDCardFile[0]);
        data.append('status', 'true');
        return data;
    }

    const generateDataEdit = () => {
        const data = new FormData();
        data.append('Id', id);
        data.append('InspectorID', dataEdit.idNumber);
        data.append('FullName', dataEdit.name);
        data.append('Type', dataEdit.type);
        data.append('Country', dataEdit.country || '');
        data.append('Oil', selectedCategory.find((x: string) => x === 'oil') ? 'true' : 'false');
        data.append('Chemicals', selectedCategory.find((x: string) => x === 'chemicals') ? 'true' : 'false');
        data.append('Gas', selectedCategory.find((x: string) => x === 'gas') ? 'true' : 'false');
        data.append('file', IDCardFile[0] || '');
        data.append('status', status.toString());
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title.includes('Cancel')) {
            navigate('/manage-inspector');
            return;
        }

        let obj: FormData;
        let response: any;

        if (id){
            obj = generateDataEdit();
            response = await putInspectorPerson(obj);
        } else {
            obj = generateDataAdd();
            response = await postInspectorPerson(obj);
        }
        
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Inspector saved.',
                description: "We've saved the inspector data for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/manage-inspector')
            }, 1000);
        } else {
            toast({
                title: 'Inspector failed to save.',
                description: "We could not save the inspector data for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: (field: string, value: any) => void) => {
        //console.log("handleDrop", acceptedFiles)
        setIDCardFile(acceptedFiles);
        //setPreviewFile(URL.createObjectURL(acceptedFiles[0]));
        setFieldValue('uploadIDCard', acceptedFiles[0]);
    };

    const handleChangeCategory = (props: any, value: boolean, type: string) => {
        let selected = selectedCategory;

        if (value) {
            if (type === 'oil' && !selectedCategory.find((x: string) => x === 'oil')) {
                selected.push('oil');
            }

            if (type === 'gas' && !selectedCategory.find((x: string) => x === 'gas')) {
                selected.push('gas');
            }

            if (type === 'chemicals' && !selectedCategory.find((x: string) => x === 'chemicals')) {
                selected.push('chemicals');
            }
        } else {
            if (type === 'oil') {
                selected = selectedCategory.filter((x: string) => x !== 'oil');
            }

            if (type === 'gas') {
                selected = selectedCategory.filter((x: string) => x !== 'gas');
            }

            if (type === 'chemicals') {
                selected = selectedCategory.filter((x: string) => x !== 'chemicals');
            }
        }

        setSelectedCategory(selected);
        props.setFieldValue('category', selected.length > 0 ? selected.toString() : '');
    }

    const handleStatus = (props: any, value: boolean) => {
        props.setFieldValue('status', value);
        setStatus(value);
    }

    const getDataDetail = async () => {
        setIsSubmitting(true);
        const response = await getInspectorPersonById(id) as any;
        if (response) {
            setDataDetail(response.data);
            setStatus(response.data.status);
            getMyCategory(response.data);
            /*if (response.data && response.data?.uriFile) {
                setPreviewFile(response.data.uriFile);
            } else {
                setPreviewFile('');
            }*/
        }
        setIsSubmitting(false);
    }

    const getMyCategory = (dataDetail: any) => {
        const selected = [] as any;

        if (dataDetail.oil) {
            selected.push('oil');
        }

        if (dataDetail.gas) {
            selected.push('gas');
        }

        if (dataDetail.chemicals) {
            selected.push('chemicals');
        }

        setSelectedCategory(selected);
        return selected.toString();
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    // otherwise the form will reset every time setState hook is called
    const initialValues = useMemo(() => ({
        type: dataDetail?.type ?? '',
        name: dataDetail?.fullName ?? '',
        country: dataDetail?.country ?? '',
        category: selectedCategory.toString(),
        idNumber: dataDetail?.inspectorID ?? '',
        uploadIDCard: null,
        status: Boolean(dataDetail?.status),
    }), [dataDetail?.country, dataDetail?.fullName, dataDetail?.inspectorID, dataDetail?.status, dataDetail?.type, selectedCategory]);

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={initialValues}
                    validationSchema={inspectorPersonSchema}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='type'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.type && form.touched.type)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCompanyType name='type' placeholder='Choose Type' handleChange={({ value }: any) => props.setFieldValue('type', value)} value={props.values.type} hasError={form.errors.type && form.touched.type} defaultValue={props.values.type} />
                                                <FormErrorMessage>{String(form.errors.type)}</FormErrorMessage>
                                            </div>
                                        </div>
                                        
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='name'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.name && form.touched.name)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Name' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.name)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='country'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.country && form.touched.country)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCountry name='country' placeholder='Choose Country' handleChange={({ value }: { value: string }) => props.setFieldValue('country', value)} value={props.values.country} hasError={Boolean(form.errors.country && form.touched.country)} defaultValue={props.values.country} isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.country)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='category'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.category && form.touched.category)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Category<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container" style={{ justifyContent: 'center' }}>
                                                <div>
                                                    <Checkbox isChecked={field.value.includes('oil')} style={{ marginRight: 30 }} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeCategory(props, e.target.checked, 'oil')}>Oil</Checkbox>
                                                    <Checkbox isChecked={field.value.includes('gas')} style={{ marginRight: 30 }} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeCategory(props, e.target.checked, 'gas')}>Gas</Checkbox>
                                                    <Checkbox isChecked={field.value.includes('chemicals')} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeCategory(props, e.target.checked, 'chemicals')}>Chemicals</Checkbox>
                                                </div>
                                                <FormErrorMessage>{String(form.errors.category)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='idNumber'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.idNumber && form.touched.idNumber)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">ID Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input ID Number' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.idNumber)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='uploadIDCard'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.uploadIDCard && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Upload ID Card</FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    fileId={dataDetail?.uploadId}
                                                    type="invoice"
                                                    title="ID Card"
                                                    handleDrop={(e) => handleDrop(e, props.setFieldValue)}
                                                    isInvalid={Boolean(form.errors.uploadIDCard && form.submitCount)}
                                                    isDisabled={props.isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.uploadIDCard)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {Boolean(id) && <Field name='status'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.status && form.touched.status)} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Status
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={status} onChange={() => handleStatus(props, !props.values.status)} id='status-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Save Inspector
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default EditInspectorPerson;
